import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Zap, Lock, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

function formatErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function AuthPage({ onAuthSuccess }) {
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/password-login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(formatErrorDetail(data.detail) || 'Login failed');
        setSubmitting(false);
        return;
      }
      const user = await res.json();
      if (typeof onAuthSuccess === 'function') {
        onAuthSuccess(user);
      } else {
        window.location.reload();
      }
    } catch (err) {
      setError(err?.message || 'Network error');
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="auth-page" style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-primary)',
      padding: 16,
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: '-20%', left: '-10%',
        width: '60%', height: '60%',
        background: 'radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}
      >
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            width: 56, height: 56,
            background: '#7C3AED',
            borderRadius: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
            boxShadow: '0 8px 32px rgba(124,58,237,0.2)',
          }}>
            <Zap size={26} color="#fff" />
          </div>
          <h1 style={{
            fontSize: 28, fontWeight: 700, color: 'var(--text-primary)',
            marginBottom: 8, letterSpacing: '-0.02em',
          }}>
            BornToBeAlive
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 15 }}>
            Private access
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            background: 'var(--bg-secondary)',
            borderRadius: 0,
            border: '1px solid var(--border-subtle)',
            padding: 32,
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          <label
            htmlFor="access-password"
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              fontSize: 13, fontWeight: 600,
              color: 'var(--text-secondary)',
              marginBottom: 10,
              letterSpacing: '0.04em', textTransform: 'uppercase',
            }}
          >
            <Lock size={14} /> Access password
          </label>
          <input
            id="access-password"
            data-testid="access-password-input"
            type="password"
            value={password}
            autoFocus
            autoComplete="current-password"
            onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
            placeholder="Enter your password"
            disabled={submitting}
            style={{
              width: '100%',
              background: 'var(--bg-depth-1)',
              border: `1px solid ${error ? '#EF4444' : 'var(--border-subtle)'}`,
              borderRadius: 0,
              padding: '14px 16px',
              fontSize: 15,
              color: 'var(--text-primary)',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.2s ease',
              boxSizing: 'border-box',
            }}
            onFocus={e => { if (!error) e.currentTarget.style.borderColor = '#7C3AED'; }}
            onBlur={e => { if (!error) e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
          />

          {error && (
            <div
              data-testid="auth-error"
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 8,
                marginTop: 12,
                padding: '10px 12px',
                background: 'rgba(239,68,68,0.08)',
                border: '1px solid rgba(239,68,68,0.25)',
                color: '#FCA5A5',
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            data-testid="password-submit-btn"
            disabled={submitting || !password}
            style={{
              width: '100%',
              marginTop: 20,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              background: submitting || !password ? 'var(--bg-depth-1)' : '#7C3AED',
              border: '1px solid #7C3AED',
              borderRadius: 0,
              padding: '14px 20px',
              color: submitting || !password ? 'var(--text-muted)' : '#fff',
              fontSize: 15, fontWeight: 600,
              cursor: submitting || !password ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit',
            }}
          >
            {submitting ? 'Verifying…' : 'Unlock platform'}
          </button>

          <p style={{
            textAlign: 'center', color: 'var(--text-muted)',
            fontSize: 12, marginTop: 20, lineHeight: 1.5,
          }}>
            This platform is private. Authorized access only.
          </p>
        </form>
      </motion.div>
    </div>
  );
}
